import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, Text } from '@aws-amplify/ui-react';

const NavigationBar = ({ signOut, username, isAuthenticated }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavigation = (page) => {
    navigate(page);
  };

  const handleLogin = () => {
    // Call the toggleAuth function from props to show the Amplify auth UI
    signOut(); // Use the existing signOut prop which toggles auth when user is not authenticated
  };

  const handleSignUp = () => {
    // Call the toggleAuth function from props to show the Amplify auth UI
    // We can add a parameter to indicate signup mode if needed
    signOut(true); // Pass true to indicate signup mode
  };

  return (
    <Flex 
      direction="column"
      backgroundColor="#2e3a46"
      width="100%"
      style={{
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}
    >
      {/* Top Section with Logo, Brand, and Auth Buttons */}
      <Flex 
        direction="row" 
        justifyContent="center"
        alignItems="center"
        padding={isAuthenticated ? (isMobile ? "0.6rem 1rem" : "1rem 2rem") : "1.5rem 2rem"}
        width="100%"
        position="relative"
        style={{
          minHeight: isAuthenticated ? 'auto' : '120px'
        }}
      >
        {/* Logo - Positioned absolutely on the left */}
        <div style={{ 
          position: 'absolute', 
          left: '2rem',
          top: '10px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'transform 0.2s'
        }}
        onClick={() => handleNavigation('/')}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
        >
          <img 
            src="/campus2career_logo.png" 
            alt="Campus2Career Logo" 
            style={{ 
              height: '100px',
              width: 'auto',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
            }}
          />
          {/* Tagline - always to the right of logo */}
          <div style={{
            color: 'white',
            marginLeft: '15px',
            fontSize: '0.85rem',
            fontWeight: '500',
            opacity: 0.9,
            letterSpacing: '0.3px',
            lineHeight: '1.2',
            textAlign: 'left'
          }}>
            <div>Smarten Your Search.</div>
            <div>Sharpen Your CV.</div>
          </div>
        </div>

        {/* Brand Name - Centered */}
        <Text
          variation="primary"
          fontSize={isMobile ? "1.8rem" : "2.5rem"}
          fontWeight="bold"
          onClick={() => handleNavigation('/')}
          style={{ 
            cursor: 'pointer',
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transition: 'all 0.2s',
            letterSpacing: '0.5px'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.textShadow = '0 3px 6px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.textShadow = '0 2px 4px rgba(0,0,0,0.2)';
          }}
        >
          Campus2Career
        </Text>

        {/* Authentication Buttons - Positioned absolutely on the right */}
        {isAuthenticated ? (
          <div style={{ 
            position: 'absolute', 
            right: isMobile ? '1rem' : '4rem',
            zIndex: 5
          }}>
            <Button
              onClick={signOut}
              variation="primary"
              size="small"
              style={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '2px solid rgba(255,255,255,0.3)',
                color: 'white',
                borderRadius: '25px',
                padding: isMobile ? '0.4rem 1rem' : '0.6rem 1.5rem',
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                fontWeight: '600',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 5px 15px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Sign Out
            </Button>
          </div>
        ) : (
          <div style={{ 
            position: 'absolute', 
            right: isMobile ? '1rem' : '4rem',
            zIndex: 5
          }}>
            <Flex direction="row" gap={isMobile ? "0.5rem" : "1rem"}>
              <Button
                variation="primary"
                onClick={handleLogin}
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid rgba(255,255,255,0.5)',
                  color: 'white',
                  borderRadius: '25px',
                  padding: isMobile ? '0.4rem 0.8rem' : '0.6rem 1.5rem',
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  fontWeight: '600',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  display: isMobile ? 'none' : 'block'  // Hide Sign In on mobile
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  e.target.style.borderColor = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Sign In
              </Button>
              <Button
                variation="primary"
                onClick={handleSignUp}
                style={{
                  backgroundColor: '#667eea',
                  border: '2px solid #667eea',
                  color: 'white',
                  borderRadius: '25px',
                  padding: isMobile ? '0.4rem 0.8rem' : '0.6rem 1.5rem',
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  fontWeight: '600',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#764ba2';
                  e.target.style.borderColor = '#764ba2';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#667eea';
                  e.target.style.borderColor = '#667eea';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                {isMobile ? 'Sign Up' : 'Create Account'}
              </Button>
            </Flex>
          </div>
        )}
      </Flex>

      {/* Navigation Links - Centered below the brand name */}
      {isAuthenticated && (
        <Flex 
          direction="row" 
          justifyContent="center"
          gap={isMobile ? "1rem" : "3rem"}
          padding={isMobile ? "0 0.5rem 1rem 0.5rem" : "0 1rem 1.2rem 1rem"}
          flexWrap={isMobile ? "wrap" : "nowrap"}
        >
          <Button
            onClick={() => handleNavigation('/AccountDetails')}
            variation="link"
            size={isMobile ? "small" : "medium"}
            style={{
              color: 'white',
              fontSize: isMobile ? '0.85rem' : '1rem',
              fontWeight: 'bold',
              textDecoration: 'none',
              padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              transition: 'all 0.3s',
              boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)',
              flex: isMobile ? '1 1 auto' : '0 0 auto',
              minWidth: isMobile ? '100px' : 'auto'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 3px 10px rgba(102, 126, 234, 0.3)';
            }}
          >
            {isMobile ? 'Account' : 'Account Details'}
          </Button>
          <Button
            onClick={() => handleNavigation('/CvAssistance')}
            variation="link"
            size={isMobile ? "small" : "medium"}
            style={{
              color: 'white',
              fontSize: isMobile ? '0.85rem' : '1rem',
              fontWeight: 'bold',
              textDecoration: 'none',
              padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              transition: 'all 0.3s',
              boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)',
              flex: isMobile ? '1 1 auto' : '0 0 auto',
              minWidth: isMobile ? '100px' : 'auto'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 3px 10px rgba(102, 126, 234, 0.3)';
            }}
          >
            {isMobile ? 'CV' : 'CV Assistance'}
          </Button>
          <Button
            onClick={() => handleNavigation('/Listings')}
            variation="link"
            size={isMobile ? "small" : "medium"}
            style={{
              color: 'white',
              fontSize: isMobile ? '0.85rem' : '1rem',
              fontWeight: 'bold',
              textDecoration: 'none',
              padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              transition: 'all 0.3s',
              boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)',
              flex: isMobile ? '1 1 auto' : '0 0 auto',
              minWidth: isMobile ? '100px' : 'auto'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 3px 10px rgba(102, 126, 234, 0.3)';
            }}
          >
            {isMobile ? 'Internships' : 'Internship Listings'}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default NavigationBar;